import { DEFAULT_ADDED_ROUTINE, LINE_STATUS, SLOTS } from "./constants";

/** Food relation strings the quotation API accepts. */
const FOOD_RELATION = {
    before: "before_food",
    after: "after_food",
    with: "with_food",
};

const toFoodRelation = (value) => {
    if (!value) return null;
    const v = String(value).toLowerCase();
    if (v.includes("before")) return FOOD_RELATION.before;
    if (v.includes("after")) return FOOD_RELATION.after;
    if (v.includes("with")) return FOOD_RELATION.with;
    return null;
};

export const foodRelationLabel = (relation) => {
    if (relation === FOOD_RELATION.before) return "Before food";
    if (relation === FOOD_RELATION.after) return "After food";
    if (relation === FOOD_RELATION.with) return "With food";
    return "Timing not stated";
};

/**
 * OCR reports a routine two ways: a structured `routine` block when it read the
 * schedule confidently, and loose `medicineInfo` text when it didn't. Prefer the
 * structured one, fall back to parsing the text.
 */
const parseFrequencyToSchedule = (frequency, dosageSize) => {
    const schedule = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    if (!frequency) return schedule;

    const f = String(frequency).toLowerCase();
    const dose = Number(dosageSize) || 1;
    const named = ["morning", "afternoon", "evening", "night"].some((slot) => f.includes(slot));

    if (named || f.includes("bed")) {
        if (f.includes("morning")) schedule.morning = dose;
        if (f.includes("afternoon")) schedule.afternoon = dose;
        if (f.includes("evening")) schedule.evening = dose;
        if (f.includes("night") || f.includes("bed")) schedule.night = dose;
    } else if (f.includes("twice") || f.includes("2 time")) {
        schedule.morning = dose;
        schedule.night = dose;
    } else if (f.includes("thrice") || f.includes("3 time")) {
        schedule.morning = dose;
        schedule.afternoon = dose;
        schedule.night = dose;
    }

    return schedule;
};

const buildRoutine = (grounded, info) => {
    const routine = grounded?.routine;

    if (routine) {
        const slots = routine.slots || {};
        const foodRelation = Object.values(slots).find((slot) => slot?.foodRelation)?.foodRelation;

        return {
            schedule: {
                morning: slots.morning?.dose ?? 0,
                afternoon: slots.afternoon?.dose ?? 0,
                evening: slots.evening?.dose ?? 0,
                night: slots.night?.dose ?? 0,
            },
            days: routine.days || undefined,
            relationToFood: toFoodRelation(foodRelation),
            frequency: (routine.frequency || "everyday").toLowerCase().slice(0, 30),
            duration: Number(routine.durationDays) > 0 ? Number(routine.durationDays) : 30,
            doseSize: Number(routine.dosePerDay) || 1,
        };
    }

    return {
        schedule: parseFrequencyToSchedule(info?.frequency, info?.dosageSize),
        relationToFood: toFoodRelation(info?.timing_of_dosage),
        frequency: (info?.frequency || "everyday").toLowerCase().slice(0, 30),
        duration: Number(info?.duration) > 0 ? Number(info.duration) : 30,
        doseSize: Number(info?.dosageSize) || 1,
    };
};

const statusFor = (grounded) => {
    if (grounded?.groundingStatus === "matched" && grounded?.match?.name) return LINE_STATUS.MATCHED;
    if ((grounded?.suggestions || []).length > 0) return LINE_STATUS.CHOOSE;
    return LINE_STATUS.UNMATCHED;
};

/** Doses per day, used to suggest a quantity from the routine. */
export const dailyDose = (schedule = {}) =>
    SLOTS.reduce((total, slot) => total + (Number(schedule[slot.key]) || 0), 0);

export const suggestedQuantity = (routine) => {
    const perDay = dailyDose(routine?.schedule);
    if (!perDay) return 1;
    return Math.max(1, Math.ceil(perDay * (routine.duration || 30)));
};

/**
 * Turn a `prescription/v2/validate` response into the rows this page edits.
 * `groundedMedicines` is the source of truth for the list; `medicineInfo` is
 * correlated by name for the bounding box and any loose routine text.
 */
export const buildLines = (data, prescriptionImageUrl) => {
    const lines = (data?.groundedMedicines || []).map((grounded, index) => {
        const info = (data?.medicineInfo || []).find((m) => m.medicine_name === grounded.originalName) || {};
        const status = statusFor(grounded);
        const routine = buildRoutine(grounded, info);

        const isMatched = status === LINE_STATUS.MATCHED;

        return {
            id: `${index}-${grounded.originalName || "line"}`,
            rawName: grounded.originalName || "Couldn't read this line",
            name: isMatched ? grounded.match.name : "",
            // `manufacturer` comes back null on most catalogue sources, but
            // `content` carries the salt and strength — the thing a pharmacist
            // actually checks a brand match against.
            detail: isMatched ? grounded.match.content || grounded.match.manufacturer || "" : "",
            medicineId: isMatched ? grounded.match.medicineId : null,
            status,
            suggestions: grounded.suggestions || [],
            isScheduleH: !!grounded.isScheduleH,
            isColdStorage: !!grounded.isColdStorage,
            drugForm: grounded.drugForm || "",
            // Bounding boxes are optional — OCR returns null for them on some
            // prescriptions, and the row simply drops the crop when it does.
            boundingBox: info.bounding_box || grounded.boundingBox || null,
            prescriptionImageUrl,
            routine,
            // Grounding computes the pack quantity itself and flags the ones it
            // wasn't sure about. Trust it, and pass the doubt on to the pharmacist.
            quantity: Number(grounded.quantity?.quantity) > 0
                ? Number(grounded.quantity.quantity)
                : suggestedQuantity(routine),
            quantityNeedsReview: !!grounded.quantity?.needsReview,
            included: isMatched,
        };
    });

    // Mark OCR duplicates — the same medicine name read twice from a prescription.
    const rawCount = {};
    lines.forEach((l) => {
        const key = (l.rawName || "").toLowerCase().trim();
        if (key) rawCount[key] = (rawCount[key] || 0) + 1;
    });

    return lines.map((l) => ({
        ...l,
        isDuplicate: (rawCount[(l.rawName || "").toLowerCase().trim()] || 0) > 1,
    }));
};

/**
 * Build a line for a medicine the pharmacist picked from the catalogue rather
 * than one OCR found — a medicine on the prescription the model missed.
 *
 * Deliberately the same shape `buildLines` produces, so the row renders through
 * the same component with no special cases beyond its subtitle.
 *
 * One gap worth knowing: catalogue search doesn't report `isScheduleH` or
 * `isColdStorage`, so an added line carries neither flag even when it warrants
 * one. The pharmacist chose this medicine by name, so they already know what it
 * is — but don't read the missing chip as a bug.
 */
export const buildAddedLine = (pick, seq) => ({
    id: `added-${seq}`,
    addedByHand: true,
    rawName: "",
    name: pick.name,
    detail: pick.content || pick.manufacturer || pick.packing || "",
    medicineId: pick.medicineId ?? pick.medicine_id ?? null,
    status: LINE_STATUS.MATCHED,
    suggestions: [],
    isScheduleH: false,
    isColdStorage: false,
    drugForm: "",
    boundingBox: null,
    prescriptionImageUrl: null,
    routine: { ...DEFAULT_ADDED_ROUTINE, schedule: { ...DEFAULT_ADDED_ROUTINE.schedule } },
    quantity: suggestedQuantity(DEFAULT_ADDED_ROUTINE),
    quantityNeedsReview: false,
    included: true,
});

/** Apply a catalogue pick (suggestion or search result) to a line. */
export const resolveLine = (line, pick) => ({
    ...line,
    name: pick.name,
    detail: pick.content || pick.manufacturer || pick.packing || "",
    medicineId: pick.medicineId ?? pick.medicine_id ?? null,
    isScheduleH: pick.isScheduleH ?? line.isScheduleH,
    isColdStorage: pick.isColdStorage ?? line.isColdStorage,
    status: LINE_STATUS.MATCHED,
    suggestions: [],
    included: true,
});

/**
 * Build the autoQuote payload. Only confirmed lines go in, and each carries its
 * resolved catalogue name — the backend re-grounds by exact name and silently
 * drops anything it can't match, so sending the raw OCR name loses the line.
 */
export const buildMedicineItems = (lines) =>
    lines
        .filter((line) => line.included && line.status === LINE_STATUS.MATCHED && line.name)
        .map((line) => ({
            itemName: line.name,
            quantity: Number(line.quantity) || 1,
            routine: {
                schedule: line.routine.schedule,
                ...(line.routine.days ? { days: line.routine.days } : {}),
                doseSize: line.routine.doseSize || 1,
                ...(line.routine.relationToFood ? { relationToFood: line.routine.relationToFood } : {}),
                frequency: line.routine.frequency || "everyday",
                duration: line.routine.duration || 30,
            },
        }));

/** Compare what was sent against what the quotation came back holding. */
export const reconcile = (sentLines, quotation) => {
    const landed = (quotation?.prescriptionItems || quotation?.items || [])
        .map((item) => item?.item?.name || item?.name || "")
        .filter(Boolean)
        .map((name) => name.toLowerCase().trim());

    if (!landed.length) return { landed: [], dropped: [], unknown: true };

    const dropped = sentLines.filter((line) => !landed.includes(line.name.toLowerCase().trim()));

    return {
        landed: sentLines.filter((line) => landed.includes(line.name.toLowerCase().trim())),
        dropped,
        unknown: false,
    };
};

/**
 * Is this prescription link a PDF?
 *
 * The API hands back signed Azure links (`…/<hash>.pdf?sv=…&sig=…`), so the
 * extension has to be read off the path, not the end of the string.
 */
export const isPdfUrl = (url) => {
    if (!url) return false;
    const path = String(url).split(/[?#]/, 1)[0];
    return path.toLowerCase().endsWith(".pdf");
};
